import styled from "styled-components";
import StatisticItem from "features/dashboard/Statistics/StatisticItem";
import AreaChartOutlined from "@ant-design/icons/AreaChartOutlined";
import PieChartOutlined from "@ant-design/icons/PieChartOutlined";
import RiseOutlined from "@ant-design/icons/RiseOutlined";
import StockOutlined from "@ant-design/icons/StockOutlined";
import Flex from "common/Boxes/Flex";
import { useSelector } from "react-redux";
import { DISPLAY_CURRENCIES, MOBILE_MENU } from "common/constants";
import React from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Title from "common/Typography/Title";
import { getAccountsTotals } from "features/dashboard/common/utils";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import useMedia from "utils/hooks/useMedia";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { ICON_TYPES } from "@shared/common/constants";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { useDashboardStatistics } from "@shared/features/dashboard/hooks";
import PnlStatisticItem from "features/dashboard/Statistics/PnlStatisticItem";
import { sharedLib } from "@shared/services/shared.service";
import { selectUniqueAccountsPortfolioData } from "@shared/slices/portfolio.slice.ts";
import { LanguageNameSpaces } from "@shared/types";

const StatisticsColumn = () => {
  const { isLoading: getAccountsLoading } =
    sharedLib.sharedApi.portfolio.endpoints.getPortfolioData.useQueryState(
      undefined
    );
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const isMobile = useMedia([`(max-width: ${MOBILE_MENU})`], [true], false);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const dashboardStatistics = useDashboardStatistics();
  const precision = getDisplayCurrencyAmountPrecision();
  const accountsData = useSelector(selectUniqueAccountsPortfolioData);

  const getSumByCurrency = (sum: number) => {
    return (
      <TextWithPrecisionAndIcon
        text={sum}
        iconType={ICON_TYPES.DISPLAY_CURRENCY}
        precision={precision}
      />
    );
  };

  const { total, last24Change } = getAccountsTotals(
    accountsData,
    displayCurrency === DISPLAY_CURRENCIES.USD
  );
  const getLast24Change = () => getSumByCurrency(last24Change);
  const getTotalAmount = () => getSumByCurrency(total);

  const tradesStatsItems = (
    <>
      <StatisticItem
        title={t("statistics.open_trades_amount")}
        icon={<AreaChartOutlined />}
        value={dashboardStatistics.tradesAmount}
      />
      <PnlStatisticItem openTradesData={dashboardStatistics.openTradesData} />
      <StatisticItem
        title={t("statistics.potential_profit")}
        icon={<RiseOutlined />}
        value={dashboardStatistics.tradesPotentialProfit}
      />
    </>
  );

  const accountsStatsItems = (
    <>
      <StatisticItem
        title={t("statistics.total_amount")}
        icon={<PieChartOutlined />}
        value={getTotalAmount()}
      />
      <StatisticItem
        title={t("statistics.last_24h_change")}
        icon={<StockOutlined />}
        value={getLast24Change()}
      />
    </>
  );

  return (
    <Wrapper $isMobile={isMobile}>
      <StatisticsTitle>{t("statistics.title")}</StatisticsTitle>
      <HorizontalBorder $isMobile={isMobile} />
      <LoadingSpinnerOverlay
        isLoading={getAccountsLoading || dashboardStatistics.isLoading}
        spinnerMarginTop={75}
        hideContentWhileLoading={true}
        backgroundColor="transparent"
      >
        <SectionTitle style={{ paddingBottom: isMobile ? 24 : 28 }}>
          {t("open_trades.title")}
        </SectionTitle>
        <SectionWrapper
          $isMobile={isMobile}
          style={{ height: isMobile ? 270 : 330 }}
        >
          {tradesStatsItems}
        </SectionWrapper>
        <HorizontalBorder $isMobile={isMobile} />
        <SectionTitle style={{ paddingBottom: isMobile ? 24 : 40 }}>
          {t("accounts.title")}
        </SectionTitle>
        <SectionWrapper
          $isMobile={isMobile}
          style={{ height: isMobile ? 180 : 220 }}
        >
          {accountsStatsItems}
        </SectionWrapper>
      </LoadingSpinnerOverlay>
    </Wrapper>
  );
};

export default StatisticsColumn;

const HorizontalBorder = styled.div<{ $isMobile: boolean }>`
  border-bottom: 1px solid ${(p) => p.theme.colors.lightBorderGrey};
  width: 100%;
  margin-bottom: ${(p) => (p.$isMobile ? "24px" : "36px")};
`;
const Wrapper = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 200px;
  min-height: ${(p) => (p.$isMobile ? "unset" : "900px")};
`;
const StatisticsTitle = styled(Title).attrs({
  level: 2,
})`
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  margin-bottom: 16px;
`;
const SectionWrapper = styled(Flex)<{ $isMobile: boolean }>`
  flex-direction: column;
  align-items: flex-start;
  padding-bottom: ${(p) => (p.$isMobile ? "36px" : "48px")};
`;
const SectionTitle = styled(Title).attrs({
  level: 3,
  type: "secondary",
})`
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;
